import { forwardRef, memo, type ChangeEvent, type ReactNode } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import type { InputProps } from '../common/index.ts';
import { InputIconsRenderer } from '../common/index.ts';
import { Input } from './Input/index.tsx';

export type InputRendererProps = InputProps & {
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  floatOnly?: boolean;
  intOnly?: boolean;
  regexp?: string | RegExp;
  decimalPlaces?: number;
};

const replaceCommas = (value: string): string => value.replace(',', '.').replace(/,/g, '');

const createFloatRegExp = (decimalPlaces: number | undefined): RegExp => {
  const decimalPlacesCountPart = decimalPlaces === undefined ? '*' : `{0,${decimalPlaces}}`;

  return new RegExp(`^\\d*[.,]?\\d${decimalPlacesCountPart}$`);
};

const isChangeAllowed = (
  value: string,
  floatOnly?: boolean,
  intOnly?: boolean,
  regexp?: string | RegExp,
  decimalPlaces?: number,
): boolean => {
  if (intOnly && !/^\d*$/.test(value)) {
    return false;
  }

  if (floatOnly) {
    const floatOnlyRegExp = createFloatRegExp(decimalPlaces);

    return floatOnlyRegExp.test(value);
  }

  if (regexp && value.length !== 0) {
    return new RegExp(regexp).test(value);
  }

  return true;
};

export const InputRenderer = memo(
  forwardRef<HTMLInputElement, InputRendererProps>(function InputRenderer(
    { iconStart, iconEnd, intOnly, floatOnly, regexp, onChange, decimalPlaces, ...inputProps },
    ref,
  ) {
    const handleChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
      if (isChangeAllowed(e.currentTarget.value, floatOnly, intOnly, regexp, decimalPlaces)) {
        const { value } = e.currentTarget;
        e.currentTarget.value = floatOnly ? replaceCommas(value) : value;
        onChange?.(e);
      }
    });

    return (
      <InputIconsRenderer iconStart={iconStart} iconEnd={iconEnd}>
        <Input ref={ref} {...inputProps} onChange={handleChange} />
      </InputIconsRenderer>
    );
  }),
);
